import {
  ACTIVE_ALERTS_ID,
  ActiveVoyagesPanel,
  CertificateOverview,
  EmptyState,
  FleetPmsCard,
  findMenuTrail,
  followLink,
  formatDay,
  hiddenMenuItems,
  officeDashboardCards,
  PageHeader,
  Panel,
  RestOverview,
  StatTile,
  StatusBadge,
  TasksPanel,
  UpcomingExpirations,
} from '@felix/ui';
import { Skeleton, Table } from '@mantine/core';
import {
  IconArrowDown,
  IconClockExclamation,
  IconFileAlert,
  IconShip,
  IconUsersGroup,
} from '@tabler/icons-react';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, type CrewMember, officeApi, type Vessel } from '../api.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { Shell } from '../components/Shell.js';
import { useDashboard } from '../dashboard-data.js';
import { summarizeFleet } from '../fleet.js';
import { homeRedirect, PATHS } from '../routing.js';
import { useSession } from '../session.js';
import { officeVoyageSource } from '../voyage-source.js';
import classes from './DashboardPage.module.css';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; vessels: Vessel[]; crew: CrewMember[] };

/**
 * Dar ekranda (768–1279 px) panel ana sütunun altına iner: sayfa başındaki bağlantı panele kaydırır
 * ve odağı panel başlığına taşır (ADR 0156 "Duyarlılık").
 */
function jumpToActiveAlerts(event: MouseEvent<HTMLAnchorElement>) {
  event.preventDefault();
  document.getElementById(ACTIVE_ALERTS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  document.getElementById(`${ACTIVE_ALERTS_ID}-title`)?.focus({ preventScroll: true });
}

/**
 * Pano (menu-yapisi.md "Dashboard"; ISM ekranlarının özet kartları dili): filo, personel, PMS,
 * belge ve dinlenme özetleri; sağda Active Alerts paneli: bana düşen işler ve uyarılar (ADR 0156).
 * Belge ve dinlenme özetleri pano uç noktasıyla gelir (M6d2).
 */
export function DashboardPage() {
  const viewable = useSession().me?.viewableModules;
  // Pano kartlarının hiçbirini göremeyen izinli ilk sayfaya gider (ADR 0067 §5)
  const redirect = homeRedirect(viewable);
  return redirect ? <Navigate to={redirect} replace /> : <Dashboard viewable={viewable} />;
}

function Dashboard({ viewable }: { viewable: readonly string[] | undefined }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Menüde gizli sayfanın verisi okunmaz, kısayolu çizilmez (ADR 0067 §4)
  const hidden = useMemo(() => hiddenMenuItems(viewable, { complete: true }), [viewable]);
  const visible = (path: string) => !findMenuTrail(path).some((item) => hidden.has(item.id));
  const seesCrew = viewable === undefined || !hidden.has('crewList');
  // Süren sefer kartları yalnız sefer okuma izniyle (ADR 0067 §4; ADR 0090 §5)
  const seesVoyages = visible('/voyage/list');

  useEffect(() => {
    Promise.all([officeApi.vessels(), seesCrew ? officeApi.crew() : { crew: [] }])
      .then(([vessels, crew]) => setState({ kind: 'ready', vessels, crew: crew.crew }))
      .catch((error: unknown) =>
        setState({ kind: 'error', code: error instanceof ApiError ? error.code : 'GENERIC' }),
      );
  }, [seesCrew]);

  const ready = state.kind === 'ready' ? state : null;
  const fleet = useMemo(() => summarizeFleet(ready?.vessels ?? []), [ready]);
  const { summary, refresh, refreshIfStale } = useDashboard();
  const cards = summary ? officeDashboardCards(summary) : null;
  const waiting = summary?.tasks?.total ?? 0;
  // Panoya dönen kişi (ör. kararı verip geri gelen) güncel işleri görür; en az 30 sn arayla
  useEffect(() => refreshIfStale(), [refreshIfStale]);
  const number = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? 'en').format(value);
  const onBoardOf = (vessel: Vessel) =>
    (ready?.crew ?? []).filter(
      (member) => member.status === 'on_board' && member.vessel?.id === vessel.id,
    ).length;

  return (
    <Shell title={t('menu.dashboard')}>
      <div className={classes.page}>
        <PageHeader title={t('menu.dashboard')} description={t('dashboard.subtitle')} />
        {state.kind === 'error' ? <ErrorAlert code={state.code} /> : null}
        {waiting > 0 ? (
          <a href={`#${ACTIVE_ALERTS_ID}`} className={classes.jump} onClick={jumpToActiveAlerts}>
            <IconArrowDown size={14} aria-hidden />
            {t('tasks.jump', { count: waiting })}
          </a>
        ) : null}

        <section className={classes.tiles} aria-label={t('dashboard.summary')}>
          {ready ? (
            <StatTile
              icon={<IconShip size={22} />}
              label={t('dashboard.tiles.vessels')}
              value={number(fleet.total)}
              wrapLabel
            />
          ) : (
            <Skeleton height={84} radius="md" />
          )}
          {/* Personel kartları pano özetinden; özet okunamazsa (yetki, bağlantı) "—" */}
          {summary === undefined ? (
            [0, 1, 2].map((n) => <Skeleton key={n} height={84} radius="md" />)
          ) : (
            <>
              <StatTile
                icon={<IconUsersGroup size={22} />}
                label={t('dashboard.tiles.onBoard')}
                value={summary ? number(summary.crew.onBoard) : '—'}
                wrapLabel
              />
              {/* Yalnız izinli kartlar (ADR 0067 §5); özet okunamazsa "—" */}
              {summary === null || summary.certificates ? (
                <StatTile
                  icon={<IconFileAlert size={22} />}
                  label={t('dashboard.tiles.certificates')}
                  wrapLabel
                  value={
                    summary?.certificates
                      ? number(summary.certificates.expired + summary.certificates.expiring)
                      : '—'
                  }
                  tone={
                    !summary?.certificates
                      ? 'default'
                      : summary.certificates.expired > 0
                        ? 'danger'
                        : summary.certificates.expiring > 0
                          ? 'warning'
                          : 'success'
                  }
                />
              ) : null}
              {summary === null || summary.rest ? (
                <StatTile
                  icon={<IconClockExclamation size={22} />}
                  label={t('dashboard.tiles.rest')}
                  wrapLabel
                  value={summary?.rest ? number(summary.rest.violation) : '—'}
                  tone={
                    !summary?.rest ? 'default' : summary.rest.violation > 0 ? 'danger' : 'success'
                  }
                />
              ) : null}
            </>
          )}
        </section>

        <div className={classes.layout}>
          <div className={classes.main}>
            <Panel
              title={t('dashboard.vessels.title')}
              flush
              extra={
                <a
                  href={PATHS.fleet}
                  className={classes.viewAll}
                  onClick={(event) => followLink(event, PATHS.fleet, navigate)}
                >
                  {t('dashboard.viewAll')}
                </a>
              }
            >
              {ready ? (
                <div className={classes.tableWrap}>
                  <Table verticalSpacing={8} horizontalSpacing="md" className={classes.table}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('dashboard.vessels.vessel')}</Table.Th>
                        <Table.Th>{t('dashboard.vessels.imo')}</Table.Th>
                        <Table.Th>{t('dashboard.vessels.crew')}</Table.Th>
                        <Table.Th>{t('dashboard.vessels.server')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {ready.vessels.map((vessel) => (
                        <Table.Tr key={vessel.id}>
                          <Table.Td className={classes.strong}>{vessel.name}</Table.Td>
                          <Table.Td>{vessel.imo ?? '—'}</Table.Td>
                          <Table.Td>{seesCrew ? number(onBoardOf(vessel)) : '—'}</Table.Td>
                          <Table.Td>
                            {vessel.activated ? (
                              <StatusBadge tone="success">{t('fleet.activated')}</StatusBadge>
                            ) : (
                              <StatusBadge tone="warning">{t('fleet.notActivated')}</StatusBadge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              ) : (
                <Skeleton height={160} m="md" />
              )}
            </Panel>

            {/* Süren seferler (V6; ADR 0090 §5): son rapor, sonraki işlem, tüketim özeti */}
            {seesVoyages ? (
              <ActiveVoyagesPanel
                source={officeVoyageSource}
                onNavigate={(path) => navigate(path)}
              />
            ) : null}

            {/* Ofis PMS kartı (PMS belgesi §22; plan M7 Task 21c): satır geminin Equipment List'i */}
            {summary?.pmsFleet ? (
              <FleetPmsCard rows={summary.pmsFleet} onOpen={(path) => navigate(path)} />
            ) : null}

            {summary?.certificates || summary?.rest ? (
              <div className={classes.pair}>
                {summary.certificates ? (
                  <CertificateOverview certificates={summary.certificates} />
                ) : null}
                {summary.rest ? <RestOverview rest={summary.rest} formatDate={formatDay} /> : null}
              </div>
            ) : null}

            {/* Yaklaşan son geçerlilikler tam genişlikte: eski "Coming next" kartı bütün modüller
                hazır olunca boş kalıyordu (ekran cilası) */}
            {summary === undefined ? (
              <Skeleton height={220} radius="md" />
            ) : summary?.certificates ? (
              <UpcomingExpirations alerts={summary.alerts} formatDate={formatDay} />
            ) : null}

            {/* Hiçbir kartın izni yok (ADR 0067 §5) */}
            {cards && !cards.any ? (
              <Panel>
                <EmptyState
                  title={t('dashboard.noCards')}
                  description={t('dashboard.noCardsHint')}
                />
              </Panel>
            ) : null}
          </div>

          <aside className={classes.side}>
            {/* Active Alerts (ADR 0156): bana düşen işler ve uyarılar; iade edilenler, ISM form
                adımları ve doküman onayları da bu paneldedir. Kısayol kartı kaldırıldı */}
            <TasksPanel summary={summary} onRefresh={() => void refresh()} />
          </aside>
        </div>
      </div>
    </Shell>
  );
}
